import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { ImpersonationStore, LogService } from 'shared';

/**
 * Adds X-Impersonate-User-Id header to DreamPay API requests when impersonating.
 * This header tells the backend to return impersonated user's data instead of the original SuperUser's.
 *
 * Also handles 403/404 errors during impersonation (e.g., user deleted) by:
 * - Showing a warning toast
 * - Ending impersonation after a delay
 */
export const impersonationInterceptor: HttpInterceptorFn = (req, next) => {
  const impersonationStore = inject(ImpersonationStore);
  const messageService = inject(MessageService);
  const logService = inject(LogService);

  const isDreampayApi = req.url.includes('dreampay.com.br/api');
  const isLogsEndpoint = req.url.includes('/api/Logs') || req.url.includes('/api/v2/logs');
  const isImpersonating = impersonationStore.isImpersonating();
  const impersonatedUser = impersonationStore.impersonatedUser();

  // Debug logging for DreamPay API requests (excluding logs endpoints to prevent infinite loop)
  if (isDreampayApi && !isLogsEndpoint) {
    logService.debug('ImpersonationInterceptor', 'Request:', {
      url: req.url,
      isDreampayApi,
      isImpersonating,
      impersonatedUserId: impersonatedUser?.id ?? 'none',
      storeState: impersonationStore.state(),
    });
  }

  // If not impersonating or not a DreamPay API request, pass through
  if (!isDreampayApi || !isImpersonating || !impersonatedUser?.id) {
    return next(req);
  }

  // Add impersonation header (skip logging for logs endpoints to prevent infinite loop)
  if (!isLogsEndpoint) {
    logService.debug('ImpersonationInterceptor', 'Adding header to:', {
      url: req.url,
      'X-Impersonate-User-Id': impersonatedUser.id,
    });
  }
  const modifiedReq = req.clone({
    setHeaders: {
      'X-Impersonate-User-Id': impersonatedUser.id,
    },
  });

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 403/404 during impersonation (user may have been deleted or access revoked)
      if (error.status === 403 || error.status === 404) {
        messageService.add({
          severity: 'warn',
          summary: 'Impersonation Ended',
          detail: 'Cannot access data for this user. Returning to your account.',
          life: 5000,
        });
        // Small delay so user sees the toast before reload
        setTimeout(() => {
          impersonationStore.endImpersonation();
        }, 1500);
      }
      return throwError(() => error);
    }),
  );
};
